/**
 * 🎮 MEGA GAMES COLLECTION - Jeux Ultra Améliorés
 * Dés, Coffre, Cible, Diamants, Cartes
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GameSounds } from './GameSounds';
import { api } from '../../services/api';
import { toast } from 'sonner';

// Configuration des probabilités (identique partout)
const PRIZE_CONFIG = {
  stamp: { probability: 0.001, label: '🏆 TAMPON!', value: 'stamp', points: 0 },
  discount10: { probability: 0.001, label: '🎁 -10%', value: 'discount10', points: 0 },
  discount5: { probability: 0.003, label: '🎁 -5%', value: 'discount5', points: 0 },
  replay: { probability: 0.03, label: '🍀 Rejoue!', value: 'replay', points: 0 },
  points50: { probability: 0.005, label: '⭐ 50 pts', value: '50', points: 50 },
  points25: { probability: 0.02, label: '⭐ 25 pts', value: '25', points: 25 },
  points10: { probability: 0.10, label: '⭐ 10 pts', value: '10', points: 10 },
  points5: { probability: 0.84, label: '✨ 5 pts', value: '5', points: 5 },
};

const determineWinning = () => {
  const random = Math.random();
  let cumulative = 0;
  for (const [key, config] of Object.entries(PRIZE_CONFIG)) {
    cumulative += config.probability;
    if (random <= cumulative) return { key, ...config };
  }
  return { key: 'points5', ...PRIZE_CONFIG.points5 };
};

const recordGame = async (customerId, gameType, winning, isTraining, onWin, onGameEnd) => {
  try {
    const prizeType = winning.points > 0 ? 'points' : 
                     winning.key === 'replay' ? 'replay' :
                     winning.key === 'stamp' ? 'stamp' :
                     winning.key.startsWith('discount') ? 'discount' : 'nothing';
    const prizeValue = winning.points > 0 ? winning.value : 
                      winning.key.startsWith('discount') ? winning.key : winning.value;
    const response = await api.recordGamePlay(customerId, gameType, prizeType, prizeValue, null, isTraining);
    if (response.message) toast.success(response.message);
    onWin?.(winning);
    onGameEnd?.();
    return response;
  } catch (error) {
    console.error('Erreur:', error);
    return null;
  }
};

// ================================================================
// 🎲 MEGA COSMIC DICE - Dés avec animation 3D réaliste
// ================================================================

const Dice3D = ({ value, isRolling, delay = 0 }) => {
  const dots = {
    1: [[1,1]], 
    2: [[0,2],[2,0]], 
    3: [[0,2],[1,1],[2,0]], 
    4: [[0,0],[0,2],[2,0],[2,2]], 
    5: [[0,0],[0,2],[1,1],[2,0],[2,2]], 
    6: [[0,0],[0,1],[0,2],[2,0],[2,1],[2,2]]
  };
  
  return (
    <motion.div
      className="relative w-20 h-20 sm:w-24 sm:h-24"
      style={{ perspective: '200px' }}
      initial={{ rotateX: 0, rotateY: 0 }}
      animate={isRolling ? {
        rotateX: [0, 360, 720, 1080, 1440],
        rotateY: [0, 180, 360, 540, 720],
        y: [0, -50, 0, -30, 0, -10, 0],
      } : { rotateX: 0, rotateY: 0 }}
      transition={isRolling ? {
        duration: 2,
        delay,
        ease: [0.25, 0.1, 0.25, 1],
      } : { duration: 0.5 }}
    >
      <div 
        className="w-full h-full rounded-2xl p-3 shadow-2xl"
        style={{
          background: 'linear-gradient(145deg, #4F46E5 0%, #7C3AED 50%, #6D28D9 100%)',
          boxShadow: '0 10px 30px rgba(79, 70, 229, 0.4), inset 0 2px 10px rgba(255,255,255,0.2)',
        }}
      >
        <div className="w-full h-full grid grid-cols-3 grid-rows-3 gap-1">
          {[0,1,2].map(row => [0,1,2].map(col => (
            <div key={`${row}-${col}`} className="flex items-center justify-center">
              {dots[value]?.some(([r,c]) => r === row && c === col) && (
                <motion.div 
                  className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-white shadow-lg"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: isRolling ? 2 + delay : 0, duration: 0.2 }}
                />
              )}
            </div>
          )))}
        </div>
      </div>
    </motion.div>
  );
};

export function MegaCosmicDice({ customerId, onWin, isTraining = false, onGameEnd }) {
  const [dice1, setDice1] = useState(1);
  const [dice2, setDice2] = useState(1);
  const [isRolling, setIsRolling] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const rollDice = async () => {
    if (isRolling) return;
    
    GameSounds.init();
    GameSounds.click();
    GameSounds.diceRoll();
    
    // Ajouter des sons de rebond pendant le lancer
    setTimeout(() => GameSounds.diceBounce(), 500);
    setTimeout(() => GameSounds.diceBounce(), 800);
    setTimeout(() => GameSounds.diceBounce(), 1100);
    
    setIsRolling(true);
    setResult(null);
    setShowConfetti(false);
    
    const winning = determineWinning();
    
    // Déterminer les valeurs des dés en fonction du gain
    let d1, d2;
    if (winning.key === 'stamp') {
      d1 = 6; d2 = 6; // Double 6 pour le jackpot
    } else if (winning.key === 'discount10') {
      d1 = 6; d2 = 5;
    } else if (winning.points >= 25) {
      d1 = Math.floor(Math.random() * 2) + 5; // 5 ou 6
      d2 = Math.floor(Math.random() * 3) + 4; // 4, 5 ou 6
    } else {
      d1 = Math.floor(Math.random() * 6) + 1;
      d2 = Math.floor(Math.random() * 6) + 1;
    }
    
    setTimeout(() => {
      setDice1(d1);
      setDice2(d2);
    }, 1800);
    
    setTimeout(async () => {
      setIsRolling(false);
      setResult(winning);
      
      if (winning.key === 'stamp' || winning.key === 'discount10') {
        GameSounds.bigWinFanfare(); // Fanfare améliorée !
        setShowConfetti(true);
      } else if (winning.key === 'replay') {
        GameSounds.replay();
      } else if (winning.key.startsWith('discount')) {
        GameSounds.giftSound();
      } else if (winning.points >= 25) {
        GameSounds.win();
      } else {
        GameSounds.coins();
      }
      
      await recordGame(customerId, 'dice', winning, isTraining, onWin, onGameEnd);
    }, 2500);
  };

  return (
    <div className="relative text-center py-6">
      {/* Confettis */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-30">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-3 h-3"
              initial={{ x: '50%', y: '50%', scale: 0 }}
              animate={{
                x: `${Math.random() * 100}%`,
                y: `${Math.random() * 100 + 50}%`,
                scale: [0, 1, 0],
                rotate: Math.random() * 720,
              }}
              transition={{ duration: 2, delay: i * 0.03 }}
              style={{
                backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#A855F7'][i % 4],
                borderRadius: Math.random() > 0.5 ? '50%' : '0%',
              }}
            />
          ))}
        </div>
      )}
      
      {isTraining && (
        <div className="mb-4 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/50 inline-block">
          <span className="text-blue-400 text-sm font-bold">🎯 MODE ENTRAÎNEMENT</span>
        </div>
      )}
      
      <h3 className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 mb-6">
        🎲 DÉS COSMIQUES 🎲
      </h3>
      
      <p className="text-gray-400 text-sm mb-6">Lance les dés et tente ta chance !</p>
      
      {/* Zone de lancer avec effet de table */}
      <div className="relative mx-auto w-64 h-40 sm:w-80 sm:h-48 mb-6 rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
          boxShadow: 'inset 0 4px 20px rgba(0,0,0,0.5), 0 0 30px rgba(79, 70, 229, 0.2)',
        }}
      >
        {/* Motif de table de jeu */}
        <div className="absolute inset-4 border-2 border-dashed border-purple-500/30 rounded-2xl" />
        
        {/* Dés */}
        <div className="absolute inset-0 flex items-center justify-center gap-6">
          <Dice3D value={dice1} isRolling={isRolling} delay={0} />
          <Dice3D value={dice2} isRolling={isRolling} delay={0.2} />
        </div>
      </div>
      
      {/* Bouton LANCER */}
      <motion.button 
        onClick={rollDice} 
        disabled={isRolling}
        className="px-10 py-4 rounded-2xl font-bold text-xl text-white shadow-lg"
        style={{ 
          background: isRolling ? '#444' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: isRolling ? 'none' : '0 6px 0 #4c1d95, 0 8px 20px rgba(102,126,234,0.4)',
        }}
        whileHover={!isRolling ? { scale: 1.05 } : {}}
        whileTap={!isRolling ? { scale: 0.98, y: 4 } : {}}
      >
        {isRolling ? (
          <span className="flex items-center gap-2">
            <motion.span animate={{ rotate: 360 }} transition={{ duration: 0.3, repeat: Infinity }}>🎲</motion.span>
            LANCEMENT...
          </span>
        ) : '🎲 LANCER !'}
      </motion.button>
      
      {/* Résultat */}
      <AnimatePresence>
        {result && (
          <motion.div 
            initial={{ opacity: 0, y: 20, scale: 0.8 }} 
            animate={{ opacity: 1, y: 0, scale: 1 }} 
            exit={{ opacity: 0 }}
            className={`mt-6 p-6 rounded-2xl mx-auto max-w-xs ${
              result.key === 'stamp' || result.key === 'discount10'
                ? 'bg-gradient-to-r from-yellow-500/30 to-orange-500/30 border-2 border-yellow-400'
                : result.points >= 25
                ? 'bg-gradient-to-r from-purple-500/30 to-pink-500/30 border border-purple-400'
                : 'bg-gray-800/50 border border-gray-600'
            }`}
          >
            <motion.span 
              className="text-4xl block mb-2"
              animate={result.key === 'stamp' ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 0.5, repeat: result.key === 'stamp' ? Infinity : 0 }}
            >
              {dice1 + dice2 === 12 ? '🎯' : dice1 + dice2 >= 10 ? '🔥' : '✨'}
            </motion.span>
            <p className="text-lg text-gray-300 mb-1">Total: {dice1 + dice2}</p>
            <p className="text-2xl font-black text-white">{result.label}</p>
            {isTraining && <p className="text-xs text-gray-400 mt-2">(Entraînement)</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ================================================================
// 📦 MEGA MYSTERY CHEST - Coffre au trésor animé
// ================================================================

export function MegaMysteryChest({ customerId, onWin, isTraining = false, onGameEnd }) {
  const [chests, setChests] = useState([false, false, false, false, false]);
  const [selectedChest, setSelectedChest] = useState(null);
  const [result, setResult] = useState(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [showContent, setShowContent] = useState(false);

  const selectChest = async (index) => {
    if (isRevealing || selectedChest !== null) return;
    
    GameSounds.init();
    GameSounds.click();
    GameSounds.suspense(); // Son de suspense !
    
    setSelectedChest(index);
    setIsRevealing(true);
    
    // Animation d'ouverture du coffre
    setTimeout(() => {
      GameSounds.chestOpen();
      setChests(prev => {
        const newChests = [...prev];
        newChests[index] = true;
        return newChests;
      });
    }, 500);
    
    const winning = determineWinning();
    
    setTimeout(() => {
      setShowContent(true);
      setResult(winning);
      
      if (winning.key === 'stamp' || winning.key === 'discount10') {
        GameSounds.bigWinFanfare(); // Fanfare améliorée !
      } else if (winning.key === 'replay') {
        GameSounds.replay();
      } else if (winning.key.startsWith('discount')) {
        GameSounds.giftSound();
      } else if (winning.points >= 25) {
        GameSounds.magicReveal(); // Son magique !
        GameSounds.win();
      } else {
        GameSounds.coins();
      }
    }, 1200);
    
    setTimeout(async () => {
      await recordGame(customerId, 'chest', winning, isTraining, onWin, onGameEnd);
      setIsRevealing(false);
    }, 1800);
  };

  const reset = () => {
    setChests([false, false, false, false, false]);
    setSelectedChest(null);
    setResult(null);
    setShowContent(false);
  };

  return (
    <div className="text-center py-6">
      {isTraining && (
        <div className="mb-4 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/50 inline-block">
          <span className="text-blue-400 text-sm font-bold">🎯 MODE ENTRAÎNEMENT</span>
        </div>
      )}
      
      <h3 className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500 mb-2">
        📦 COFFRE MYSTÈRE 📦
      </h3>
      <p className="text-gray-400 text-sm mb-6">Choisis un coffre et découvre ton trésor !</p>
      
      {/* Coffres */}
      <div className="flex justify-center gap-3 sm:gap-4 flex-wrap mb-6">
        {chests.map((isOpen, index) => (
          <motion.button
            key={index}
            onClick={() => selectChest(index)}
            disabled={isRevealing || selectedChest !== null}
            className="relative w-16 h-16 sm:w-20 sm:h-20"
            whileHover={selectedChest === null ? { scale: 1.1, y: -5 } : {}}
            whileTap={selectedChest === null ? { scale: 0.95 } : {}}
          >
            {/* Coffre fermé ou ouvert */}
            <motion.div
              className="w-full h-full rounded-xl flex items-center justify-center text-4xl sm:text-5xl"
              style={{
                background: selectedChest === index 
                  ? 'linear-gradient(145deg, #FFD700 0%, #FFA500 100%)'
                  : 'linear-gradient(145deg, #8B4513 0%, #654321 100%)',
                boxShadow: selectedChest === index 
                  ? '0 0 30px rgba(255,215,0,0.5)'
                  : '0 4px 15px rgba(0,0,0,0.3)',
              }}
              animate={isOpen ? { rotateY: [0, -20, 0] } : {}}
              transition={{ duration: 0.3 }}
            >
              {isOpen ? (showContent && selectedChest === index ? (
                <motion.span
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  {result?.key === 'stamp' ? '🏆' : 
                   result?.key === 'replay' ? '🍀' :
                   result?.key.startsWith('discount') ? '🎁' : '⭐'}
                </motion.span>
              ) : '✨') : '📦'}
            </motion.div>
            
            {/* Numéro du coffre */}
            <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs text-gray-500">
              #{index + 1}
            </span>
          </motion.button>
        ))}
      </div>
      
      {/* Résultat */}
      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className={`p-6 rounded-2xl mx-auto max-w-xs ${
              result.key === 'stamp' || result.key === 'discount10'
                ? 'bg-gradient-to-r from-yellow-500/30 to-orange-500/30 border-2 border-yellow-400'
                : result.points >= 25
                ? 'bg-gradient-to-r from-amber-500/30 to-yellow-500/30 border border-amber-400'
                : 'bg-gray-800/50 border border-gray-600'
            }`}
          >
            <p className="text-2xl font-black text-white">{result.label}</p>
            {isTraining && <p className="text-xs text-gray-400 mt-2">(Entraînement)</p>}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Bouton rejouer */}
      {selectedChest !== null && !isRevealing && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={reset}
          className="mt-4 px-6 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold"
        >
          🔄 Rejouer
        </motion.button>
      )}
    </div>
  );
}

// ================================================================
// 🏹 MEGA ARCHERY - Tir à l'arc FPS (Vue première personne)
// ================================================================

// Composant SVG de l'arc tenu à la main (vue FPS)
const BowFPS = ({ drawProgress, isAiming }) => {
  // drawProgress: 0 = repos, 1 = arc tendu au max
  const stringPull = drawProgress * 60; // Combien la corde est tirée
  
  return (
    <svg viewBox="0 0 200 300" className="w-full h-full">
      <defs>
        <linearGradient id="woodGrain" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8B4513"/>
          <stop offset="30%" stopColor="#A0522D"/>
          <stop offset="60%" stopColor="#8B4513"/>
          <stop offset="100%" stopColor="#654321"/>
        </linearGradient>
        <linearGradient id="handSkin" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DEB887"/>
          <stop offset="50%" stopColor="#D2B48C"/>
          <stop offset="100%" stopColor="#BC9F77"/>
        </linearGradient>
        <filter id="bowShadow">
          <feDropShadow dx="2" dy="4" stdDeviation="3" floodOpacity="0.4"/>
        </filter>
      </defs>
      
      {/* Main qui tient l'arc (en bas à droite) */}
      <g transform="translate(90, 180)">
        {/* Poignet/bras */}
        <ellipse cx="60" cy="100" rx="35" ry="25" fill="url(#handSkin)" />
        
        {/* Paume de la main */}
        <ellipse cx="45" cy="70" rx="28" ry="35" fill="url(#handSkin)" />
        
        {/* Pouce */}
        <ellipse cx="15" cy="50" rx="12" ry="20" fill="url(#handSkin)" transform="rotate(-30, 15, 50)"/>
        
        {/* Doigts (tenant la poignée de l'arc) */}
        <ellipse cx="30" cy="25" rx="8" ry="25" fill="url(#handSkin)" transform="rotate(-10, 30, 25)"/>
        <ellipse cx="50" cy="20" rx="7" ry="28" fill="url(#handSkin)" transform="rotate(5, 50, 20)"/>
        <ellipse cx="68" cy="25" rx="7" ry="25" fill="url(#handSkin)" transform="rotate(15, 68, 25)"/>
        <ellipse cx="82" cy="35" rx="6" ry="20" fill="url(#handSkin)" transform="rotate(25, 82, 35)"/>
        
        {/* Protection de bras (bracer) */}
        <rect x="40" y="85" width="45" height="30" rx="5" fill="#5D3A1A" stroke="#8B4513" strokeWidth="2"/>
        <line x1="50" y1="88" x2="50" y2="112" stroke="#3D2A1A" strokeWidth="2"/>
        <line x1="65" y1="88" x2="65" y2="112" stroke="#3D2A1A" strokeWidth="2"/>
        <line x1="80" y1="88" x2="80" y2="112" stroke="#3D2A1A" strokeWidth="2"/>
      </g>
      
      {/* Arc en bois (vertical) */}
      <g filter="url(#bowShadow)">
        {/* Branche supérieure de l'arc */}
        <path 
          d={`M 100 150 Q ${80 - drawProgress * 15} 100, ${70 - drawProgress * 10} 30 Q ${60 - drawProgress * 8} 10, ${75 - drawProgress * 5} 5`} 
          fill="none" 
          stroke="url(#woodGrain)" 
          strokeWidth="12"
          strokeLinecap="round"
        />
        
        {/* Branche inférieure de l'arc */}
        <path 
          d={`M 100 150 Q ${80 - drawProgress * 15} 200, ${70 - drawProgress * 10} 270 Q ${60 - drawProgress * 8} 290, ${75 - drawProgress * 5} 295`} 
          fill="none" 
          stroke="url(#woodGrain)" 
          strokeWidth="12"
          strokeLinecap="round"
        />
        
        {/* Poignée (grip) */}
        <rect x="92" y="140" width="16" height="25" rx="4" fill="#3D2A1A" stroke="#5D3A1A" strokeWidth="1"/>
        
        {/* Corde de l'arc */}
        <line 
          x1={75 - drawProgress * 5} y1="8" 
          x2={100 + stringPull} y2="152" 
          stroke="#8B8B83" 
          strokeWidth="2"
        />
        <line 
          x1={100 + stringPull} y1="152" 
          x2={75 - drawProgress * 5} y2="292" 
          stroke="#8B8B83" 
          strokeWidth="2"
        />
        
        {/* Flèche encochée */}
        {isAiming && (
          <g transform={`translate(${100 + stringPull}, 152)`}>
            {/* Fût de la flèche */}
            <line x1="0" y1="0" x2="-120" y2="0" stroke="#CD853F" strokeWidth="4"/>
            
            {/* Pointe de flèche (métal) */}
            <polygon points="-120,0 -135,-6 -135,6" fill="#708090" stroke="#505050" strokeWidth="1"/>
            
            {/* Empennage (plumes) */}
            <polygon points="0,-2 15,-8 15,-2" fill="#DC143C" opacity="0.9"/>
            <polygon points="0,2 15,8 15,2" fill="#DC143C" opacity="0.9"/>
            <polygon points="0,0 15,-5 15,5" fill="#8B0000" opacity="0.7"/>
            
            {/* Encoche */}
            <rect x="-3" y="-3" width="6" height="6" fill="#5D3A1A"/>
          </g>
        )}
      </g>
      
      {/* Indicateur de tension (cercle qui pulse) */}
      {isAiming && drawProgress > 0.5 && (
        <circle 
          cx="100" 
          cy="152" 
          r={10 + drawProgress * 15} 
          fill="none" 
          stroke={drawProgress > 0.8 ? "#22C55E" : "#EAB308"} 
          strokeWidth="2"
          opacity={0.5 + drawProgress * 0.3}
        />
      )}
    </svg>
  );
};

// Composant Flèche qui vole vers la cible
const FlyingArrowFPS = ({ isFlying, onHit }) => {
  if (!isFlying) return null;
  
  return (
    <motion.div
      className="absolute left-1/2 -translate-x-1/2 pointer-events-none z-20"
      initial={{ bottom: '15%', scale: 1, opacity: 1 }}
      animate={{ 
        bottom: '65%', 
        scale: 0.3, 
        opacity: [1, 1, 0.9, 0]
      }}
      transition={{ duration: 0.35, ease: 'easeIn' }}
      onAnimationComplete={onHit}
    >
      {/* Flèche stylisée volant vers la cible */}
      <div className="relative">
        <div className="text-4xl" style={{ transform: 'rotate(-90deg)' }}>➳</div>
        {/* Traînée de mouvement */}
        <motion.div 
          className="absolute top-full left-1/2 -translate-x-1/2 w-1"
          initial={{ height: 0 }}
          animate={{ height: 60 }}
          style={{ background: 'linear-gradient(to bottom, #fbbf24, transparent)' }}
        />
      </div>
    </motion.div>
  );
};

export function MegaSpinningTarget({ customerId, onWin, isTraining = false, onGameEnd }) {
  const [isAiming, setIsAiming] = useState(false);
  const [drawProgress, setDrawProgress] = useState(0);
  const [isFlying, setIsFlying] = useState(false);
  const [result, setResult] = useState(null);
  const [hasShot, setHasShot] = useState(false);
  const [showImpact, setShowImpact] = useState(false);
  const [impactPosition, setImpactPosition] = useState({ x: 50, y: 50 });
  const drawIntervalRef = useRef(null);

  // Commencer à viser (maintenir le bouton)
  const startAiming = () => {
    if (hasShot) return;
    
    GameSounds.init();
    setIsAiming(true);
    GameSounds.bowDraw();
    
    // Augmenter progressivement la tension
    let progress = 0;
    drawIntervalRef.current = setInterval(() => {
      progress += 0.04;
      if (progress >= 1) {
        progress = 1;
        clearInterval(drawIntervalRef.current);
      }
      setDrawProgress(progress);
    }, 50);
  };

  // Relâcher la flèche
  const release = async () => {
    if (hasShot || !isAiming) return;
    
    clearInterval(drawIntervalRef.current);
    setIsAiming(false);
    setHasShot(true);
    
    // Son de tir
    GameSounds.bowRelease();
    
    // Animation de la flèche
    setIsFlying(true);
    
    const winning = determineWinning();
    
    // Calculer la position d'impact basée sur le gain
    let impactX, impactY;
    if (winning.key === 'stamp' || winning.key === 'discount10') {
      // Bullseye ! Centre exact
      impactX = 50 + (Math.random() - 0.5) * 5;
      impactY = 50 + (Math.random() - 0.5) * 5;
    } else if (winning.points >= 25) {
      // Proche du centre
      impactX = 50 + (Math.random() - 0.5) * 20;
      impactY = 50 + (Math.random() - 0.5) * 20;
    } else {
      // Zone externe
      impactX = 50 + (Math.random() - 0.5) * 50;
      impactY = 50 + (Math.random() - 0.5) * 50;
    }
    setImpactPosition({ x: impactX, y: impactY });
    
    // Stocker le résultat pour après l'animation
    setTimeout(() => {
      setResult(winning);
    }, 350);
  };

  const handleArrowHit = () => {
    setIsFlying(false);
    setShowImpact(true);
    
    GameSounds.arrowImpact();
    
    const winning = result;
    if (!winning) return;
    
    if (winning.key === 'stamp' || winning.key === 'discount10') {
      setTimeout(() => GameSounds.bullseye(), 100);
      setTimeout(() => GameSounds.bigWinFanfare(), 300);
    } else if (winning.key === 'replay') {
      setTimeout(() => GameSounds.replay(), 100);
    } else if (winning.key.startsWith('discount')) {
      setTimeout(() => GameSounds.giftSound(), 100);
    } else if (winning.points >= 25) {
      setTimeout(() => GameSounds.bullseye(), 100);
      setTimeout(() => GameSounds.win(), 200);
    } else {
      setTimeout(() => GameSounds.coins(), 100);
    }
    
    setTimeout(async () => {
      await recordGame(customerId, 'target', winning, isTraining, onWin, onGameEnd);
    }, 500);
  };

  const reset = () => {
    setDrawProgress(0);
    setIsAiming(false);
    setIsFlying(false);
    setShowImpact(false);
    setResult(null);
    setHasShot(false);
    setImpactPosition({ x: 50, y: 50 });
  };

  return (
    <div className="text-center py-4">
      {isTraining && (
        <div className="mb-3 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/50 inline-block">
          <span className="text-blue-400 text-sm font-bold">🎯 MODE ENTRAÎNEMENT</span>
        </div>
      )}
      
      <h3 className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 mb-1">
        🏹 TIR À L'ARC 🎯
      </h3>
      <p className="text-gray-400 text-xs sm:text-sm mb-3">Maintiens pour bander l'arc, relâche pour tirer !</p>
      
      {/* Zone de jeu - Vue FPS */}
      <div 
        className="relative w-full max-w-md h-80 sm:h-96 mx-auto mb-4 rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #87CEEB 0%, #98D8AA 40%, #4A7C59 70%, #2D4A30 100%)',
        }}
      >
        {/* Ciel et nuages */}
        <div className="absolute top-0 left-0 right-0 h-1/3 overflow-hidden">
          <div className="absolute top-4 left-8 w-16 h-8 bg-white/60 rounded-full blur-sm"/>
          <div className="absolute top-8 left-20 w-24 h-10 bg-white/50 rounded-full blur-sm"/>
          <div className="absolute top-2 right-12 w-20 h-8 bg-white/40 rounded-full blur-sm"/>
        </div>
        
        {/* Arbres en arrière-plan */}
        <div className="absolute top-1/4 left-0 right-0 h-1/4">
          <div className="absolute bottom-0 left-4 w-0 h-0 border-l-[20px] border-r-[20px] border-b-[50px] border-l-transparent border-r-transparent border-b-[#228B22]"/>
          <div className="absolute bottom-0 left-10 w-0 h-0 border-l-[25px] border-r-[25px] border-b-[60px] border-l-transparent border-r-transparent border-b-[#2E8B57]"/>
          <div className="absolute bottom-0 right-4 w-0 h-0 border-l-[22px] border-r-[22px] border-b-[55px] border-l-transparent border-r-transparent border-b-[#228B22]"/>
          <div className="absolute bottom-0 right-12 w-0 h-0 border-l-[18px] border-r-[18px] border-b-[45px] border-l-transparent border-r-transparent border-b-[#2E8B57]"/>
        </div>
        
        {/* Sol/Herbe */}
        <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-[#2D4A30] to-[#4A7C59]"/>
        
        {/* CIBLE - Au centre, en face */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-28 h-28 sm:w-36 sm:h-36">
          {/* Support en bois de la cible */}
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-4 h-12 bg-gradient-to-r from-[#8B4513] to-[#A0522D]"/>
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-16 h-3 bg-[#654321] rounded"/>
          
          {/* La cible elle-même */}
          <svg viewBox="0 0 200 200" className="w-full h-full drop-shadow-xl">
            {/* Fond blanc */}
            <circle cx="100" cy="100" r="95" fill="#FFFFFF" stroke="#333" strokeWidth="3"/>
            {/* Anneau noir externe */}
            <circle cx="100" cy="100" r="90" fill="none" stroke="#1a1a1a" strokeWidth="8"/>
            {/* Anneau blanc */}
            <circle cx="100" cy="100" r="75" fill="#FFFFFF" stroke="#333" strokeWidth="1"/>
            {/* Anneau bleu */}
            <circle cx="100" cy="100" r="60" fill="#1E90FF" stroke="#333" strokeWidth="1"/>
            {/* Anneau bleu clair */}
            <circle cx="100" cy="100" r="45" fill="#87CEEB" stroke="#333" strokeWidth="1"/>
            {/* Anneau rouge */}
            <circle cx="100" cy="100" r="30" fill="#DC143C" stroke="#333" strokeWidth="1"/>
            {/* Centre jaune (bullseye) */}
            <circle cx="100" cy="100" r="15" fill="#FFD700" stroke="#333" strokeWidth="1"/>
            {/* Point central */}
            <circle cx="100" cy="100" r="5" fill="#000"/>
          </svg>
          
          {/* Flèche plantée dans la cible */}
          {showImpact && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 15 }}
              className="absolute pointer-events-none"
              style={{
                left: `${impactPosition.x}%`,
                top: `${impactPosition.y}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              {/* Flèche plantée */}
              <div className="relative">
                <div className="w-3 h-3 bg-[#CD853F] rounded-full border-2 border-[#8B4513]"/>
                {/* Empennage visible */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[15px] border-l-transparent border-r-transparent border-b-red-600"/>
              </div>
            </motion.div>
          )}
        </div>
        
        {/* RÉTICULE de visée (rouge) */}
        {!hasShot && (
          <motion.div 
            className="absolute top-1/4 left-1/2 -translate-x-1/2 w-16 h-16 sm:w-20 sm:h-20 pointer-events-none z-10"
            animate={{ 
              scale: isAiming ? [1, 1.1, 1] : 1,
              opacity: isAiming ? 1 : 0.7
            }}
            transition={{ duration: 0.5, repeat: isAiming ? Infinity : 0 }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              {/* Cercle externe */}
              <circle cx="50" cy="50" r="40" fill="none" stroke="#DC143C" strokeWidth="3" opacity="0.8"/>
              {/* Croix de visée */}
              <line x1="50" y1="5" x2="50" y2="35" stroke="#DC143C" strokeWidth="3"/>
              <line x1="50" y1="65" x2="50" y2="95" stroke="#DC143C" strokeWidth="3"/>
              <line x1="5" y1="50" x2="35" y2="50" stroke="#DC143C" strokeWidth="3"/>
              <line x1="65" y1="50" x2="95" y2="50" stroke="#DC143C" strokeWidth="3"/>
              {/* Point central */}
              <circle cx="50" cy="50" r="4" fill="#DC143C"/>
            </svg>
          </motion.div>
        )}
        
        {/* Flèche qui vole */}
        <FlyingArrowFPS isFlying={isFlying} onHit={handleArrowHit} />
        
        {/* ARC ET MAIN - En bas à droite (vue FPS) */}
        <div className="absolute -bottom-4 -right-4 w-48 h-64 sm:w-56 sm:h-72 pointer-events-none">
          <BowFPS drawProgress={drawProgress} isAiming={isAiming} />
        </div>
        
        {/* Jauge de puissance */}
        {isAiming && (
          <div className="absolute bottom-3 left-3 right-3">
            <div className="h-3 bg-black/40 rounded-full overflow-hidden backdrop-blur-sm border border-white/20">
              <motion.div
                className="h-full rounded-full"
                style={{
                  background: drawProgress > 0.8 
                    ? 'linear-gradient(90deg, #22C55E, #EAB308, #EF4444)' 
                    : drawProgress > 0.5 
                    ? 'linear-gradient(90deg, #22C55E, #EAB308)'
                    : '#22C55E',
                  width: `${drawProgress * 100}%`,
                }}
              />
            </div>
            <p className="text-xs text-center mt-1 text-white font-bold drop-shadow-lg">
              {drawProgress < 0.3 ? '💪 Tire plus fort...' : drawProgress < 0.6 ? '🎯 Continue...' : drawProgress < 0.85 ? '🔥 Bien !' : '⭐ PARFAIT !'}
            </p>
          </div>
        )}
      </div>
      
      {/* Bouton TIRER (maintenir) */}
      {!hasShot ? (
        <motion.button
          onMouseDown={startAiming}
          onMouseUp={release}
          onMouseLeave={() => { if (isAiming) release(); }}
          onTouchStart={startAiming}
          onTouchEnd={release}
          className="px-8 sm:px-10 py-3 sm:py-4 rounded-2xl font-bold text-lg sm:text-xl text-white select-none"
          style={{
            background: isAiming 
              ? 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)'
              : 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            boxShadow: isAiming 
              ? '0 2px 0 #7F1D1D, 0 4px 10px rgba(239,68,68,0.3)'
              : '0 6px 0 #991B1B, 0 8px 20px rgba(239,68,68,0.4)',
          }}
          whileHover={{ scale: 1.02 }}
          animate={{ y: isAiming ? 4 : 0 }}
        >
          {isAiming ? '🏹 RELÂCHE POUR TIRER !' : '🎯 MAINTIENS POUR VISER'}
        </motion.button>
      ) : (
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={reset}
          className="px-6 py-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white font-bold"
        >
          🔄 Rejouer
        </motion.button>
      )}
      
      {/* Résultat */}
      <AnimatePresence>
        {result && showImpact && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: 'spring', stiffness: 300 }}
            className={`mt-4 p-4 sm:p-6 rounded-2xl mx-auto max-w-xs ${
              result.key === 'stamp' || result.key === 'discount10'
                ? 'bg-gradient-to-r from-yellow-500/30 to-orange-500/30 border-2 border-yellow-400'
                : result.points >= 25
                ? 'bg-gradient-to-r from-red-500/30 to-orange-500/30 border border-red-400'
                : 'bg-gray-800/50 border border-gray-600'
            }`}
          >
            <motion.span 
              className="text-3xl sm:text-4xl block mb-1"
              animate={result.key === 'stamp' ? { scale: [1, 1.2, 1], rotate: [0, 5, -5, 0] } : {}}
              transition={{ duration: 0.5, repeat: result.key === 'stamp' ? Infinity : 0 }}
            >
              {result.key === 'stamp' ? '🏆' : result.points >= 25 ? '🎯' : '✨'}
            </motion.span>
            <p className="text-xl sm:text-2xl font-black text-white">{result.label}</p>
            {isTraining && <p className="text-xs text-gray-400 mt-1">(Entraînement)</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ================================================================
// 💎 MEGA DIAMOND HUNT - Chasse aux diamants
// ================================================================

export function MegaDiamondHunt({ customerId, onWin, isTraining = false, onGameEnd }) {
  const [gems, setGems] = useState(Array(9).fill({ revealed: false, type: null }));
  const [selectedGem, setSelectedGem] = useState(null);
  const [result, setResult] = useState(null);
  const [isRevealing, setIsRevealing] = useState(false);

  const gemTypes = ['💎', '💎', '💎', '🪨', '🪨', '🪨', '🪨', '⭐', '🏆'];

  const selectGem = async (index) => {
    if (isRevealing || selectedGem !== null) return;
    
    GameSounds.init();
    GameSounds.click();
    GameSounds.scratch(); // Son de grattage !
    
    setSelectedGem(index);
    setIsRevealing(true);
    
    const winning = determineWinning();
    
    // Révéler la gemme sélectionnée
    setTimeout(() => {
      const gemType = winning.key === 'stamp' ? '🏆' : 
                     winning.points >= 25 ? '💎' : 
                     winning.key === 'replay' ? '⭐' : '💎';
      
      setGems(prev => {
        const newGems = [...prev];
        newGems[index] = { revealed: true, type: gemType };
        return newGems;
      });
      
      GameSounds.gemReveal(); // Son de révélation de gemme !
      
      if (winning.key === 'stamp' || winning.key === 'discount10') {
        setTimeout(() => GameSounds.bigWinFanfare(), 200); // Fanfare !
      } else if (winning.key === 'replay') {
        GameSounds.replay();
      } else if (winning.key.startsWith('discount')) {
        GameSounds.giftSound();
      } else if (winning.points >= 25) {
        setTimeout(() => GameSounds.starTwinkle(), 100);
        setTimeout(() => GameSounds.win(), 200);
      } else {
        GameSounds.coins();
      }
      
      setResult(winning);
    }, 500);
    
    setTimeout(async () => {
      await recordGame(customerId, 'diamond', winning, isTraining, onWin, onGameEnd);
      setIsRevealing(false);
    }, 1200);
  };

  const reset = () => {
    setGems(Array(9).fill({ revealed: false, type: null }));
    setSelectedGem(null);
    setResult(null);
  };

  return (
    <div className="text-center py-6">
      {isTraining && (
        <div className="mb-4 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/50 inline-block">
          <span className="text-blue-400 text-sm font-bold">🎯 MODE ENTRAÎNEMENT</span>
        </div>
      )}
      
      <h3 className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 mb-2">
        💎 CHASSE AUX DIAMANTS 💎
      </h3>
      <p className="text-gray-400 text-sm mb-6">Trouve les diamants cachés dans la mine !</p>
      
      {/* Grille de gemmes */}
      <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto mb-6 p-4 rounded-2xl"
        style={{
          background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f23 100%)',
          boxShadow: 'inset 0 4px 20px rgba(0,0,0,0.5)',
        }}
      >
        {gems.map((gem, index) => (
          <motion.button
            key={index}
            onClick={() => selectGem(index)}
            disabled={isRevealing || selectedGem !== null}
            className="aspect-square rounded-xl flex items-center justify-center text-3xl sm:text-4xl"
            style={{
              background: gem.revealed 
                ? 'linear-gradient(145deg, #0EA5E9 0%, #6366F1 100%)'
                : 'linear-gradient(145deg, #374151 0%, #1F2937 100%)',
              boxShadow: gem.revealed 
                ? '0 0 20px rgba(14,165,233,0.5)'
                : '0 4px 10px rgba(0,0,0,0.3)',
            }}
            whileHover={selectedGem === null ? { scale: 1.05 } : {}}
            whileTap={selectedGem === null ? { scale: 0.95 } : {}}
          >
            {gem.revealed ? (
              <motion.span
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                {gem.type}
              </motion.span>
            ) : (
              <span className="text-gray-600">❓</span>
            )}
          </motion.button>
        ))}
      </div>
      
      {/* Résultat */}
      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-6 rounded-2xl mx-auto max-w-xs ${
              result.key === 'stamp' || result.key === 'discount10'
                ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 border-2 border-cyan-400'
                : result.points >= 25
                ? 'bg-gradient-to-r from-purple-500/30 to-blue-500/30 border border-purple-400'
                : 'bg-gray-800/50 border border-gray-600'
            }`}
          >
            <p className="text-2xl font-black text-white">{result.label}</p>
            {isTraining && <p className="text-xs text-gray-400 mt-2">(Entraînement)</p>}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Bouton rejouer */}
      {selectedGem !== null && !isRevealing && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={reset}
          className="mt-4 px-6 py-2 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white font-bold"
        >
          🔄 Rejouer
        </motion.button>
      )}
    </div>
  );
}

// ================================================================
// 🃏 MEGA CARD PICK - Choix de cartes animé
// ================================================================

export function MegaCardPick({ customerId, onWin, isTraining = false, onGameEnd }) {
  const [cards, setCards] = useState([
    { id: 0, flipped: false, value: null },
    { id: 1, flipped: false, value: null },
    { id: 2, flipped: false, value: null },
  ]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [result, setResult] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);

  const cardBacks = ['🂠', '🂠', '🂠'];
  const cardValues = ['🏆', '⭐', '💫'];

  const selectCard = async (index) => {
    if (isFlipping || selectedCard !== null) return;
    
    GameSounds.init();
    GameSounds.cardShuffle(); // Son de mélange de cartes !
    
    setSelectedCard(index);
    setIsFlipping(true);
    
    const winning = determineWinning();
    
    // Déterminer la valeur de la carte
    let cardValue;
    if (winning.key === 'stamp') cardValue = '🏆';
    else if (winning.points >= 25) cardValue = '⭐';
    else cardValue = '💫';
    
    setTimeout(() => {
      GameSounds.cardFlip(); // Son de carte retournée
      setCards(prev => {
        const newCards = [...prev];
        newCards[index] = { ...newCards[index], flipped: true, value: cardValue };
        return newCards;
      });
      
      setTimeout(() => {
        if (winning.key === 'stamp' || winning.key === 'discount10') {
          GameSounds.bigWinFanfare(); // Fanfare !
        } else if (winning.key === 'replay') {
          GameSounds.replay();
        } else if (winning.key.startsWith('discount')) {
          GameSounds.giftSound();
        } else if (winning.points >= 25) {
          GameSounds.magicReveal(); // Révélation magique !
          GameSounds.win();
        } else {
          GameSounds.coins();
        }
      }, 200);
      
      setResult(winning);
    }, 400);
    
    setTimeout(async () => {
      await recordGame(customerId, 'cards', winning, isTraining, onWin, onGameEnd);
      setIsFlipping(false);
    }, 1000);
  };

  const reset = () => {
    setCards([
      { id: 0, flipped: false, value: null },
      { id: 1, flipped: false, value: null },
      { id: 2, flipped: false, value: null },
    ]);
    setSelectedCard(null);
    setResult(null);
  };

  return (
    <div className="text-center py-6">
      {isTraining && (
        <div className="mb-4 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-500/50 inline-block">
          <span className="text-blue-400 text-sm font-bold">🎯 MODE ENTRAÎNEMENT</span>
        </div>
      )}
      
      <h3 className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500 mb-2">
        🃏 LE BON CHOIX 🃏
      </h3>
      <p className="text-gray-400 text-sm mb-6">Choisis une carte et découvre ton destin !</p>
      
      {/* Cartes */}
      <div className="flex justify-center gap-4 sm:gap-6 mb-6">
        {cards.map((card, index) => (
          <motion.button
            key={card.id}
            onClick={() => selectCard(index)}
            disabled={isFlipping || selectedCard !== null}
            className="relative w-20 h-28 sm:w-24 sm:h-36"
            style={{ perspective: '1000px' }}
            whileHover={selectedCard === null ? { y: -10, scale: 1.05 } : {}}
          >
            <motion.div
              className="absolute inset-0 w-full h-full"
              animate={{ rotateY: card.flipped ? 180 : 0 }}
              transition={{ duration: 0.6, type: 'spring' }}
              style={{ transformStyle: 'preserve-3d' }}
            >
              {/* Face arrière */}
              <div
                className="absolute inset-0 rounded-xl flex items-center justify-center text-5xl sm:text-6xl backface-hidden"
                style={{
                  background: 'linear-gradient(145deg, #16A34A 0%, #15803D 100%)',
                  boxShadow: '0 8px 20px rgba(22,163,74,0.3)',
                  backfaceVisibility: 'hidden',
                }}
              >
                🂠
              </div>
              
              {/* Face avant */}
              <div
                className="absolute inset-0 rounded-xl flex items-center justify-center text-5xl sm:text-6xl"
                style={{
                  background: card.value === '🏆' 
                    ? 'linear-gradient(145deg, #FFD700 0%, #FFA500 100%)'
                    : 'linear-gradient(145deg, #FFF 0%, #F3F4F6 100%)',
                  boxShadow: card.value === '🏆'
                    ? '0 0 30px rgba(255,215,0,0.5)'
                    : '0 8px 20px rgba(0,0,0,0.2)',
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                }}
              >
                {card.value || '?'}
              </div>
            </motion.div>
          </motion.button>
        ))}
      </div>
      
      {/* Résultat */}
      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-6 rounded-2xl mx-auto max-w-xs ${
              result.key === 'stamp' || result.key === 'discount10'
                ? 'bg-gradient-to-r from-yellow-500/30 to-green-500/30 border-2 border-yellow-400'
                : result.points >= 25
                ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400'
                : 'bg-gray-800/50 border border-gray-600'
            }`}
          >
            <p className="text-2xl font-black text-white">{result.label}</p>
            {isTraining && <p className="text-xs text-gray-400 mt-2">(Entraînement)</p>}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Bouton rejouer */}
      {selectedCard !== null && !isFlipping && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={reset}
          className="mt-4 px-6 py-2 rounded-full bg-green-500 hover:bg-green-600 text-white font-bold"
        >
          🔄 Rejouer
        </motion.button>
      )}
    </div>
  );
}
